import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../axiosInstance';
import './MatchingResultPage.css';

/* Helper für Datumsformat */
function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' });
  } catch {
    return iso || '';
  }
}

/* ---------- Hübsches, zugängliches Bestätigungs-Modal ---------- */
function ConfirmModal({
  open,
  title = 'Bestätigen',
  message = '',
  confirmLabel = 'Ja',
  cancelLabel = 'Abbrechen',
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-desc"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />
      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-gray-900 border border-gray-700 shadow-2xl">
        <div className="p-5">
          <h3 id="confirm-title" className="text-lg font-semibold text-white">
            {title}
          </h3>
          <p id="confirm-desc" className="mt-3 text-sm text-gray-300 whitespace-pre-line">
            {message}
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-xl bg-gray-700 text-gray-100 hover:bg-gray-600 transition"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl bg-mentixxgreen text-black font-semibold hover:bg-green-400 transition"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Ein kleines Promise-basiertes Confirm-API im Page-Component */
function useConfirmModal() {
  const [state, setState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Ja',
    cancelLabel: 'Abbrechen',
    resolver: null,
  });

  const confirm = ({ title, message, confirmLabel = 'Ja', cancelLabel = 'Abbrechen' }) =>
    new Promise((resolve) => {
      setState({
        open: true,
        title,
        message,
        confirmLabel,
        cancelLabel,
        resolver: resolve,
      });
    });

  const handleConfirm = () => {
    state.resolver && state.resolver(true);
    setState((s) => ({ ...s, open: false, resolver: null }));
  };
  const handleCancel = () => {
    state.resolver && state.resolver(false);
    setState((s) => ({ ...s, open: false, resolver: null }));
  };

  const modal = (
    <ConfirmModal
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, modal };
}

function PopupButton({ label, items }) {
  const { t } = useTranslation();
  const [showPopup, setShowPopup] = useState(false);
  const buttonRef = useRef(null);
  const [alignRight, setAlignRight] = useState(false);

  function togglePopup() {
    if (buttonRef.current) {
      const buttonPosition = buttonRef.current.getBoundingClientRect();
      setAlignRight(buttonPosition.left > window.innerWidth / 2);
    }
    setShowPopup((prev) => !prev);
  }

  function closePopup() {
    setShowPopup(false);
  }

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        onClick={togglePopup}
        className="bg-mentixxgreen text-black font-bold py-1 px-4 rounded-2xl hover:bg-green-400 transition"
      >
        {label} ({items.length})
      </button>
      {showPopup && (
        <div className={`popup-menu ${alignRight ? 'align-right' : 'align-left'}`}>
          <div className="popup-content">
            <ul className="text-sm">
              {items.map((item, index) => (
                <li key={index} className="mb-1">- {item}</li>
              ))}
            </ul>
            <button onClick={closePopup} className="mt-2 text-xs text-gray-400 hover:text-gray-200">
              {t('matchingResult.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCard({ data, surveyId, lockedAt, isSimulation, activeLockPers, confirmFn, resultId }) {
  const { t } = useTranslation();
  const {
    menteeId,
    mentorId,
    overallScore = 0,
    fachScore = 0,
    personalityScore = 0,
    weightProfile: wp = 50, // Fachlich-Anteil aus dem Matching-Server
    menteeAvatar,
    mentorAvatar,
    menteeAvatarPath = '',
    mentorAvatarPath = '',
    menteeName = t('matchingResult.mentee'),
    mentorName = t('matchingResult.mentor'),
    menteeAbteilung = '',
    menteePosition = '',
    menteeExperience = '',
    mentorAbteilung = '',
    mentorPosition = '',
    mentorExperience = '',
    goalsIntersection = [],
    competencesIntersection = [],
    menteeApplicationFields = [],
    mentorApplicationFields = [],
    menteeMethodsSkills = [],
    mentorMethodsSkills = [],
  } = data;

  // survey_id=28: Schnittmengen client-seitig berechnen
  const applicationFieldsIntersection = surveyId === 28
    ? menteeApplicationFields.filter(v => mentorApplicationFields.includes(v))
    : [];
  const methodsSkillsIntersection = surveyId === 28
    ? menteeMethodsSkills.filter(v => mentorMethodsSkills.includes(v))
    : [];

  // Persönlichkeit = 100 - fachlich
  const personalityWeight = Math.max(0, Math.min(100, 100 - Number(wp)));

  function getScoreColor(score) {
    const num = Number(score);
    if (num < 70) return 'neon-red';
    if (num < 85) return 'neon-yellow';
    return 'neon-green';
  }

  function resolveAvatar(path, inline) {
    const base = process.env.REACT_APP_IMAGE_BASE_URL || '/mentoring';
    return path
      ? `${base}/uploads/${path}`
      : inline?.startsWith('data:')
      ? inline
      : `${base}/uploads/default-avatar.png`;
  }

  async function handleAcceptMatching() {
    const sid = Number(surveyId);
    if (!sid || Number.isNaN(sid)) {
      alert(t('matchingResult.invalidSurveyId'));
      return;
    }

    // Hübsches Modal bei Simulation
    if (isSimulation && typeof confirmFn === 'function') {
      const ok = await confirmFn({
        title: 'Simulation bestätigen',
        message:
          `Dieses Matching basiert auf Simulationswerten (abweichend von der aktiven Konfiguration).\n\n` +
          `Verwendet: Persönlichkeit ${Number(personalityWeight)}%` +
          (typeof activeLockPers === 'number' ? `\nAktiver Lock: Persönlichkeit ${Number(activeLockPers)}%` : '') +
          (lockedAt ? `\nGespeichert: ${fmtDate(lockedAt)}` : '') +
          `\n\nMöchten Sie das Matching trotzdem annehmen und speichern?`,
        confirmLabel: 'Ja, trotzdem speichern',
        cancelLabel: 'Abbrechen',
      });
      if (!ok) return;
    }

    try {
      await axios.post('/mentoring/matchings', {
        resultId,
        menteeId,
        mentorId,
        surveyId: sid,
        sharedGoals: goalsIntersection,
        sharedCompetences: competencesIntersection,
        score: Number(overallScore),
        fachlicher_score: Number(fachScore),
        persoenlicher_score: Number(personalityScore)
      });
      alert(t('matchingResult.matchingSaved'));
    } catch (error) {
      const resp = error?.response;
      if (resp?.status === 409 && resp?.data?.error === 'mentee_already_matched') {
        const max = resp.data.max_mentors_per_mentee;
        alert(t('matchingResult.menteeMaxMentorsReached', { max: max ?? '' }));
      } else {
        alert(t('matchingResult.matchingSaveError'));
      }
    }
  }

  return (
    <div className="match-card-pair">
      <div className="profile-card">
        <img src={resolveAvatar(menteeAvatarPath, menteeAvatar)} alt={t('matchingResult.menteeAvatarAlt')} className="profile-pic" />
        <h2>{menteeName}</h2>
        <p>{t('matchingResult.mentee')}</p>
        <p><strong>{t('matchingResult.department')}:</strong> {menteeAbteilung || t('matchingResult.undefinedValue')}</p>
        <p><strong>{t('matchingResult.position')}:</strong> {menteePosition || t('matchingResult.undefinedValue')}</p>
        <p><strong>{t('matchingResult.experience')}:</strong> {menteeExperience || t('matchingResult.undefinedValue')}</p>
        <div className="mt-2">
          <PopupButton
            label={surveyId === 28 ? 'Anwendungsfelder' : t('matchingResult.goals')}
            items={surveyId === 28 ? applicationFieldsIntersection : goalsIntersection}
          />
        </div>
        <div className="mt-2">
          <PopupButton
            label={surveyId === 28 ? 'Methoden / Skills' : t('matchingResult.competences')}
            items={surveyId === 28 ? methodsSkillsIntersection : competencesIntersection}
          />
        </div>
      </div>

      <div className="match-center">
        <div className={`match-circle ${getScoreColor(overallScore)}`}>
          <span className="match-percent">{Number(overallScore).toFixed(2)}%</span>
        </div>

        {/* Anzeige NUR hier unter dem Score */}
        <p className="match-sub">
          {isSimulation ? (
            <>
              <strong>Simulation:</strong> Persönlichkeit {Number(personalityWeight)}%
              {typeof activeLockPers === 'number' && (
                <> · aktiver Lock: Persönlichkeit {Number(activeLockPers)}%</>
              )}
              {lockedAt && <> · gespeichert: {fmtDate(lockedAt)}</>}
            </>
          ) : (
            <>
              Konfiguration: Persönlichkeit {Number(personalityWeight)}% · gespeichert: {fmtDate(lockedAt)}
            </>
          )}
        </p>

        <button className="accept-match-button" onClick={handleAcceptMatching}>
          {t('matchingResult.acceptMatching')}
        </button>
      </div>

      <div className="profile-card">
        <img src={resolveAvatar(mentorAvatarPath, mentorAvatar)} alt={t('matchingResult.mentorAvatarAlt')} className="profile-pic" />
        <h2>{mentorName}</h2>
        <p>{t('matchingResult.mentor')}</p>
        <p><strong>{t('matchingResult.department')}:</strong> {mentorAbteilung || t('matchingResult.undefinedValue')}</p>
        <p><strong>{t('matchingResult.position')}:</strong> {mentorPosition || t('matchingResult.undefinedValue')}</p>
        <p><strong>{t('matchingResult.experience')}:</strong> {mentorExperience || t('matchingResult.undefinedValue')}</p>
        <div className="mt-2">
          <PopupButton
            label={surveyId === 28 ? 'Anwendungsfelder' : t('matchingResult.goals')}
            items={surveyId === 28 ? applicationFieldsIntersection : goalsIntersection}
          />
        </div>
        <div className="mt-2">
          <PopupButton
            label={surveyId === 28 ? 'Methoden / Skills' : t('matchingResult.competences')}
            items={surveyId === 28 ? methodsSkillsIntersection : competencesIntersection}
          />
        </div>
      </div>
    </div>
  );
}

function groupByMentee(matches) {
  const grouped = {};
  matches.forEach(match => {
    const key = String(match.menteeId ?? match.menteeName);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(match);
  });
  Object.keys(grouped).forEach(key => {
    grouped[key].sort((a, b) => Number(b.overallScore) - Number(a.overallScore));
    grouped[key] = grouped[key].slice(0, 3);
  });
  return Object.values(grouped);
}

function AutomatedMatchingButtons({ matches, surveyId, isSimulation, confirmFn, resultId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function getBestPairs() {
    const usedMentees = new Set();
    const usedMentors = new Set();
    const sorted = [...matches].filter(m => m.overallScore >= 75).sort((a, b) => b.overallScore - a.overallScore);
    const result = [];
    for (const match of sorted) {
      if (!usedMentees.has(match.menteeId) && !usedMentors.has(match.mentorId)) {
        usedMentees.add(match.menteeId);
        usedMentors.add(match.mentorId);
        result.push(match);
      }
    }
    return result;
  }

  function getMostPairs() {
    const menteeMap = new Map();
    const sorted = [...matches].filter(m => m.overallScore >= 75).sort((a, b) => b.overallScore - a.overallScore);
    const usedMentors = new Set();
    for (const match of sorted) {
      if (!menteeMap.has(match.menteeId) && !usedMentors.has(match.mentorId)) {
        menteeMap.set(match.menteeId, match);
        usedMentors.add(match.mentorId);
      }
    }
    return Array.from(menteeMap.values());
  }

  async function handleAutomatedMatching(mode) {
    // Bestätigungsdialog bei Simulation
    if (isSimulation && typeof confirmFn === 'function') {
      const ok = await confirmFn({
        title: 'Simulation bestätigen',
        message:
          'Diese Auswahl basiert auf Simulationswerten (abweichend von der aktiven Konfiguration).\n\n' +
          'Möchten Sie die vorgeschlagenen Paarungen trotzdem speichern?',
        confirmLabel: 'Ja, trotzdem speichern',
        cancelLabel: 'Abbrechen',
      });
      if (!ok) return;
    }

    setLoading(true);
    let pairs = [];
    if (mode === 'best') pairs = getBestPairs();
    if (mode === 'most') pairs = getMostPairs();

    if (pairs.length === 0) {
      alert(t('matchingResult.noPairsFound'));
      setLoading(false);
      return;
    }

    try {
      await axios.post('/mentoring/matchings/automated', {
        persist: true,
        resultId,
        surveyId,
        pairs: pairs.map(p => ({
          menteeId: p.menteeId,
          mentorId: p.mentorId,
          surveyId,
          sharedGoals: p.goalsIntersection,
          sharedCompetences: p.competencesIntersection,
          score: Number(p.overallScore),
          fachlicher_score: Number(p.fachScore),
          persoenlicher_score: Number(p.personalityScore)
        }))
      });
      alert(t('matchingResult.automatedMatchingSaved', { count: pairs.length }));
    } catch (err) {
      const resp = err?.response;
      if (resp?.status === 409 && resp?.data?.error === 'mentee_already_matched') {
        const max = resp.data.max_mentors_per_mentee;
        alert(t('matchingResult.menteeMaxMentorsReached', { max: max ?? '' }));
      } else {
        alert(t('matchingResult.automatedMatchingError'));
      }
    }
    setLoading(false);
  }

  return (
    <div className="flex gap-4 mb-6">
      <button
        className="bg-mentixxgreen text-black px-5 py-2 rounded-xl font-bold hover:bg-green-500"
        disabled={loading}
        onClick={() => handleAutomatedMatching('best')}
      >
        {t('matchingResult.bestPairs')}
      </button>
      <button
        className="bg-mentixxgreen text-black px-5 py-2 rounded-xl font-bold hover:bg-green-500"
        disabled={loading}
        onClick={() => handleAutomatedMatching('most')}
      >
        {t('matchingResult.mostPairs')}
      </button>
      {loading && <span className="text-mentixxgreen ml-3">{t('matchingResult.matchingInProgress')}</span>}
    </div>
  );
}

function MatchingResultPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchPairs, setMatchPairs] = useState([]);
  const [config, setConfig] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [activeLockWp, setActiveLockWp] = useState(null); // Fachlich-Anteil aus aktivem Lock
  const resultId = searchParams.get('resultId');
  const surveyId = parseInt(searchParams.get('surveyId'), 10);
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const isSimulation = config?.source === 'simulation';
  const activeLockPers = typeof activeLockWp === 'number' ? (100 - activeLockWp) : null;

  const { confirm, modal } = useConfirmModal();

  const scrollRight = () => {
    if (sliderRef.current) {
      const width = sliderRef.current.offsetWidth;
      sliderRef.current.scrollBy({ left: width, behavior: 'smooth' });
      setCurrentSlide((prev) => Math.min(prev + 1, matchPairs.length - 1));
    }
  };

  const scrollLeft = () => {
    if (sliderRef.current) {
      const width = sliderRef.current.offsetWidth;
      sliderRef.current.scrollBy({ left: -width, behavior: 'smooth' });
      setCurrentSlide((prev) => Math.max(prev - 1, 0));
    }
  };

  useEffect(() => {
    if (!resultId) {
      setError(t('matchingResult.noResultId'));
      setLoading(false);
      return;
    }
    async function loadMatchResult() {
      try {
        const url = `/matching/match/result?resultId=${resultId}`;
        const response = await axios.get(url, { withCredentials: true });
        const pairs = response.data.matches || [];
        setMatches(pairs);
        const grouped = groupByMentee(pairs);
        setMatchPairs(grouped);

        setConfig(response.data?.config || null);
        setCreatedAt(response.data?.createdAt || null);
      } catch (err) {
        console.error('loadMatchResult error', err);
        setError(t('matchingResult.loadError'));
      } finally {
        setLoading(false);
      }
    }
    loadMatchResult();
  }, [resultId, t]);

  // Bei Simulation: aktiven Lock (fachlich) vom Server nachladen, um ihn anzeigen zu können
  useEffect(() => {
    if (!isSimulation || !Number.isFinite(surveyId)) {
      setActiveLockWp(null);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`/matching/match/config/${surveyId}`, { withCredentials: true });
        if (r.data?.exists && typeof r.data.weightProfile === 'number') {
          setActiveLockWp(Number(r.data.weightProfile)); // Fachlich-Anteil
        } else {
          setActiveLockWp(null);
        }
      } catch (e) {
        console.error('read active lock for result page failed', e);
        setActiveLockWp(null);
      }
    })();
  }, [isSimulation, surveyId]);

  if (loading) return <div className="loading">{t('matchingResult.loading')}</div>;
  if (error) return <div className="error">{error}</div>;
  if (matchPairs.length === 0) return <div className="error">{t('matchingResult.noData')}</div>;

  return (
    <>
      <div className="matching-result-container">
        <h2>{t('matchingResult.title')}</h2>

        {/* Obere Konfig-Zeile entfernt: Anzeige erfolgt nur unter dem Score je Karte */}

        <div className="mb-4">
          <h3>{t('matchingResult.automatedMatching')}</h3>
          <AutomatedMatchingButtons
            matches={matches}
            surveyId={surveyId}
            isSimulation={isSimulation}
            confirmFn={confirm}
            resultId={resultId}
          />
        </div>

        <div className="slider-wrapper">
          <button className="slider-button left" onClick={scrollLeft}>&lt;</button>
          <div className="horizontal-slider" ref={sliderRef}>
            {matchPairs.map((group, groupIndex) => (
              <div key={groupIndex} className="match-group-slide-vertical">
                {group.map((data, index) => (
                  <div key={index} className="match-row">
                    <MatchCard
                      data={data}
                      surveyId={surveyId}
                      lockedAt={config?.lockedAt || createdAt}
                      isSimulation={isSimulation}
                      activeLockPers={activeLockPers}
                      confirmFn={confirm}
                      resultId={resultId}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <button className="slider-button right" onClick={scrollRight}>&gt;</button>
        </div>
      </div>

      <div className="slide-indicator-wrapper">
        {matchPairs.map((_, index) => (
          <div key={index} className={`slide-indicator-dot ${index === currentSlide ? 'active' : ''}`}></div>
        ))}
      </div>

      {/* Modal wird hier gerendert */}
      {modal}
    </>
  );
}

export default MatchingResultPage;
